import type { AxiosError } from 'axios';

type ApiErrorBody = {
  message?: unknown;
  error?: unknown;
  errors?: unknown;
};

const AXIOS_STATUS_MESSAGE = /^Request failed with status(?: code)? \d+$/i;

const cleanString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const findMessage = (value: unknown, depth = 0): string | undefined => {
  if (depth > 3) return undefined;

  const directMessage = cleanString(value);
  if (directMessage) return directMessage;

  if (Array.isArray(value)) {
    for (const item of value) {
      const nestedMessage = findMessage(item, depth + 1);
      if (nestedMessage) return nestedMessage;
    }
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return (
      findMessage(record.message, depth + 1) ||
      findMessage(record.error, depth + 1) ||
      findMessage(record.errors, depth + 1) ||
      findMessage(record.body, depth + 1) ||
      findMessage(record.data, depth + 1) ||
      findMessage(record.detail, depth + 1) ||
      Object.entries(record).reduce<string | undefined>(
        (message, [key, item]) =>
          message ||
          ([
            'timestamp',
            'timestamp_ms',
            'elapsed',
            'headers',
            'status',
            'status_code',
            'success',
            'code',
          ].includes(key)
            ? undefined
            : findMessage(item, depth + 1)),
        undefined,
      )
    );
  }

  return undefined;
};

const getResponseMessage = (error: unknown): string | undefined => {
  const err = error as AxiosError<ApiErrorBody>;
  const data = err?.response?.data;

  return (
    findMessage(data?.message) ||
    findMessage(data?.error) ||
    findMessage(data?.errors) ||
    findMessage(data)
  );
};

/**
 * Some BizPlus endpoints answer HTTP 200 even when they fail and put the real
 * status in the body, so only a body that says 200 counts as done.
 */
export const isExplicitApiSuccess = (res: any): boolean =>
  res?.status === 200 && Number(res?.data?.status) === 200;

/**
 * Extracts API error message for display. Prefer backend messages when present.
 */
export function getApiMessage(
  error: unknown,
  fallback = 'Something went wrong',
): string {
  const responseMessage = getResponseMessage(error);
  if (responseMessage) return responseMessage;

  if (error instanceof Error && !AXIOS_STATUS_MESSAGE.test(error.message)) {
    const errorMessage = cleanString(error.message);
    if (errorMessage)
      return errorMessage === 'Network Error'
        ? 'Network Error, Please check your internet settings'
        : errorMessage;
  }

  return fallback;
}

/**
 * Machine-readable error code from an API error body (`{ status, code, message }`).
 * Only the response body is consulted: Axios's own `error.code`
 * (e.g. `ERR_BAD_REQUEST`) is never a product error code.
 */
export function getApiErrorCode(error: unknown): string | undefined {
  const data = (error as AxiosError<{ code?: unknown }>)?.response?.data;
  const code = data?.code;
  return typeof code === 'string' && code.trim() ? code.trim() : undefined;
}

/** HTTP status of an API error, when the request reached the server. */
export function getApiErrorStatus(error: unknown): number | undefined {
  const status = (error as AxiosError)?.response?.status;
  return typeof status === 'number' ? status : undefined;
}

/** Parsed response body of an API error, typed by the caller. */
export function getApiErrorData<T = unknown>(error: unknown): T | undefined {
  return (error as AxiosError<T>)?.response?.data;
}

/**
 * Keeps the original Axios error shape intact while making `error.message`
 * useful for existing UI call sites that already read it.
 */
export function normalizeAxiosError(error: unknown): Error {
  const responseMessage = getResponseMessage(error);

  if (error instanceof Error) {
    if (responseMessage) {
      error.message = responseMessage;
    }
    if (error.message === 'Network Error') {
      error.message = 'Network Error, Please check your internet settings';
    }
    return error;
  }

  return new Error(responseMessage || getApiMessage(error));
}
